/**
 * CollegamentiCard — i legami con le altre persone della rubrica.
 *
 * È ciò che trasforma un elenco di schede in una RETE: da Dario arrivi a Romano,
 * Vincenzo e Davide, e capisci al volo che stai guardando un gruppo — un'impresa
 * familiare, dei soci, delle persone che lavorano insieme — invece di quattro
 * schede che non si parlano.
 *
 * Due specie di legame:
 * - i COLLEGAMENTI (`collegamenti`), uguali sulle due schede: «stessa azienda»,
 *   «socio», «parente»;
 * - le PRESENTAZIONI (`presentato_da`), che hanno una direzione: «Ha presentato»
 *   sulla scheda di chi presenta, «Su presentazione di» su quella di chi è stato
 *   presentato. Prima erano un collegamento «presentato da», uguale sulle due
 *   schede, e sulla scheda di chi presentava si leggeva al contrario.
 *
 * ⚠️ I nomi NON sono salvati nel legame: si risolvono dall'elenco delle
 * persone già caricato dalla pagina. Denormalizzarli sembrerebbe comodo, ma al
 * primo nome corretto (e i nomi si correggono) i due valori divergerebbero.
 *
 * ⚠️ Nessun legame viene dedotto dal sistema: cognome uguale o stessa azienda NON
 * bastano (Paolicelli/Paolocelli lo dimostra). I collegamenti li mette l'utente.
 */

import React, { useMemo, useState } from 'react';
import {
  Autocomplete, Box, Button, Chip, CircularProgress, Paper, Stack,
  TextField, Tooltip, Typography, alpha,
} from '@mui/material';
import { Add, Hub, LinkOff } from '@mui/icons-material';
import { tokens } from '../../theme';
import {
  collegaPersone, scollegaPersone, segnaPresentazione, togliPresentazione,
} from '../../services/reteContattiService';
import type { PersonaRete } from '../../types/reteContatti';

/**
 * Legami più frequenti: suggerimenti, non un elenco chiuso. Chi ha presentato chi
 * non è qui: ha una direzione e due voci sue (`vociPresentazione`).
 */
const TIPI_SUGGERITI = [
  'stessa azienda',
  'parente',
  'lavorano insieme',
  'socio',
  'ex collega',
];

const nomeBreve = (p: PersonaRete) => p.nome.trim().split(/\s+/)[0];

/**
 * Le due voci con la direzione, scritte col nome della scheda aperta: la riga
 * «[persona scelta] [che legame?]» si legge da sinistra e dice chi ha presentato
 * chi. Sulla scheda di Giovanni: «Romano Paolicelli · ha presentato Giovanni».
 */
function vociPresentazione(persona: PersonaRete) {
  return {
    /** La persona scelta ha presentato quella della scheda. */
    lHaPresentata: `ha presentato ${nomeBreve(persona)}`,
    /** La persona scelta è arrivata da quella della scheda. */
    presentataDaLei: `su presentazione di ${nomeBreve(persona)}`,
  };
}

/** Un «presentato da» scritto a mano tornerebbe un legame senza direzione: si ferma. */
const sembraPresentazione = (testo: string) => /presentat|presentazion/i.test(testo);

interface Props {
  persona: PersonaRete;
  /** Tutte le persone in rubrica: servono a risolvere i nomi e a scegliere. */
  tutte: PersonaRete[];
  onCambiato: () => Promise<void>;
  /** Apre la scheda di un'altra persona (navigazione dentro la rete). */
  onApriPersona: (id: string) => void;
  showSnack: (msg: string, severity: 'success' | 'error') => void;
}

export const CollegamentiCard: React.FC<Props> = ({
  persona, tutte, onCambiato, onApriPersona, showSnack,
}) => {
  const [scelta, setScelta] = useState<PersonaRete | null>(null);
  const [tipo, setTipo] = useState('');
  const [lavorando, setLavorando] = useState(false);

  const perId = useMemo(() => new Map(tutte.map((p) => [p.id, p])), [tutte]);
  const collegamenti = persona.collegamenti ?? [];
  const presentatori = persona.presentato_da ?? [];
  /** Chi ha presentato questa persona lo dice la scheda di ognuno di loro: si cerca qui il proprio id. */
  const presentati = useMemo(
    () => tutte.filter((p) => p.id !== persona.id && (p.presentato_da ?? []).includes(persona.id)),
    [tutte, persona.id],
  );
  const voci = vociPresentazione(persona);
  const totale = collegamenti.length + presentatori.length + presentati.length;

  /** Candidati: tutti tranne sé stesso e chi è già legato in un modo o nell'altro. */
  const disponibili = useMemo(() => {
    const esclusi = new Set([
      persona.id,
      ...collegamenti.map((c) => c.persona_id),
      ...presentatori,
      ...presentati.map((p) => p.id),
    ]);
    return tutte
      .filter((p) => !esclusi.has(p.id))
      .sort((a, b) => a.nome.localeCompare(b.nome, 'it', { sensitivity: 'base' }));
  }, [tutte, persona.id, collegamenti, presentatori, presentati]);

  const esegui = async (azione: () => Promise<void>, riuscito: string, fallito: string) => {
    setLavorando(true);
    try {
      await azione();
      await onCambiato();
      showSnack(riuscito, 'success');
      return true;
    } catch (e) {
      showSnack(e instanceof Error ? e.message : fallito, 'error');
      return false;
    } finally {
      setLavorando(false);
    }
  };

  const collega = async () => {
    if (!scelta) return;
    const legame = tipo.trim();
    let azione: () => Promise<void>;
    let riuscito: string;
    if (legame === voci.lHaPresentata) {
      azione = () => segnaPresentazione(persona, scelta.id);
      riuscito = `Segnato: ${scelta.nome} ha presentato ${persona.nome}`;
    } else if (legame === voci.presentataDaLei) {
      azione = () => segnaPresentazione(scelta, persona.id);
      riuscito = `Segnato: ${persona.nome} ha presentato ${scelta.nome}`;
    } else if (sembraPresentazione(legame)) {
      showSnack(
        `Per chi ha presentato chi scegli «${voci.lHaPresentata}» o «${voci.presentataDaLei}»: dicono la direzione`,
        'error',
      );
      return;
    } else {
      azione = () => collegaPersone(persona, scelta, legame || undefined);
      riuscito = `Collegata a ${scelta.nome}`;
    }
    if (await esegui(azione, riuscito, 'Collegamento non riuscito')) {
      setScelta(null);
      setTipo('');
    }
  };

  const scollega = (altroId: string) => {
    const altro = perId.get(altroId);
    if (!altro) return;
    void esegui(() => scollegaPersone(persona, altro), `Scollegata da ${altro.nome}`, 'Operazione non riuscita');
  };

  /** Toglie una presentazione: si scrive sempre sulla scheda di chi è stato presentato. */
  const togli = (presentatoId: string, presentatoreId: string) =>
    void esegui(() => togliPresentazione(presentatoId, presentatoreId), 'Presentazione tolta', 'Operazione non riuscita');

  const stileChip = {
    height: 30, fontSize: '0.75rem', cursor: 'pointer',
    bgcolor: alpha(tokens.color.tierBusiness.main, 0.08),
    color: tokens.color.tierBusiness.main,
    border: `1px solid ${alpha(tokens.color.tierBusiness.main, 0.25)}`,
  };

  // Una persona eliminata si dice, non si nasconde: un legame verso il nulla è
  // un'informazione, non un errore.
  const chipEliminata = (chiave: string, onDelete: () => void) => (
    <Chip
      key={chiave}
      label="persona eliminata"
      size="small"
      onDelete={lavorando ? undefined : onDelete}
      sx={{ height: 30, fontSize: '0.75rem', color: tokens.color.mist }}
    />
  );

  const chipPersona = (altro: PersonaRete, dettaglio: string | undefined, suggerimento: string, onDelete: () => void) => (
    <Tooltip key={altro.id} title={suggerimento}>
      <Chip
        label={
          <Box component="span">
            {altro.nome}
            {dettaglio && (
              <Box component="span" sx={{ color: tokens.color.mist, ml: 0.5 }}>
                · {dettaglio}
              </Box>
            )}
          </Box>
        }
        size="small"
        onClick={() => onApriPersona(altro.id)}
        onDelete={lavorando ? undefined : onDelete}
        deleteIcon={<LinkOff sx={{ fontSize: 15 }} />}
        sx={stileChip}
      />
    </Tooltip>
  );

  const riga = (titolo: string, chips: React.ReactNode[]) => (
    <Box sx={{ mb: 1.5 }}>
      <Typography variant="caption" sx={{ color: tokens.color.mist, display: 'block', mb: 0.5 }}>
        {titolo}
      </Typography>
      {/* Solo `gap`: con `spacing` la seconda riga dei chip partiva rientrata. */}
      <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.75 }}>
        {chips}
      </Stack>
    </Box>
  );

  return (
    <Paper
      variant="outlined"
      sx={{ p: 2, borderColor: tokens.color.line, bgcolor: tokens.color.paper }}
    >
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
        <Hub fontSize="small" sx={{ color: tokens.color.tierBusiness.main }} />
        <Typography
          sx={{
            fontSize: '0.62rem', fontWeight: 700, letterSpacing: '0.1em',
            textTransform: 'uppercase', color: tokens.color.mist, flex: 1,
          }}
        >
          Collegata a {totale > 0 ? `(${totale})` : ''}
        </Typography>
      </Stack>

      {presentatori.length > 0 && riga('Su presentazione di', presentatori.map((id) => {
        const altro = perId.get(id);
        return altro
          ? chipPersona(altro, undefined, `ha presentato ${persona.nome}`, () => togli(persona.id, id))
          : chipEliminata(id, () => togli(persona.id, id));
      }))}

      {presentati.length > 0 && riga('Ha presentato', presentati.map((altro) =>
        chipPersona(altro, undefined, `su presentazione di ${persona.nome}`, () => togli(altro.id, persona.id)),
      ))}

      {collegamenti.length > 0 && riga(
        presentatori.length + presentati.length > 0 ? 'Altri legami' : 'Legami',
        collegamenti.map((c) => {
          const altro = perId.get(c.persona_id);
          if (!altro) return chipEliminata(c.persona_id, () => scollega(c.persona_id));
          return chipPersona(
            altro,
            c.tipo,
            c.tipo ? `${c.tipo}${c.nota ? ` — ${c.nota}` : ''}` : 'apri la scheda',
            () => scollega(c.persona_id),
          );
        }),
      )}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="flex-start">
        <Autocomplete
          size="small"
          options={disponibili}
          value={scelta}
          onChange={(_e, v) => setScelta(v)}
          getOptionLabel={(p) => `${p.nome}${p.azienda_testo ? ` — ${p.azienda_testo}` : ''}`}
          sx={{ flex: 1, minWidth: 150, width: { xs: '100%', sm: 'auto' } }}
          renderInput={(params) => (
            <TextField {...params} placeholder="Collega a un'altra persona…" />
          )}
          noOptionsText="Nessun'altra persona in rubrica"
        />
        <Autocomplete
          size="small"
          freeSolo
          options={[voci.lHaPresentata, voci.presentataDaLei, ...TIPI_SUGGERITI]}
          value={tipo}
          onInputChange={(_e, v) => setTipo(v)}
          sx={{ flex: 1, minWidth: 150, width: { xs: '100%', sm: 'auto' } }}
          renderInput={(params) => <TextField {...params} placeholder="che legame?" />}
        />
        <Button
          variant="outlined"
          startIcon={lavorando ? <CircularProgress size={14} /> : <Add />}
          onClick={collega}
          disabled={!scelta || lavorando}
          sx={{ textTransform: 'none', minHeight: 40, flexShrink: 0 }}
        >
          collega
        </Button>
      </Stack>

      {totale === 0 && (
        <Typography variant="caption" sx={{ color: tokens.color.mist, display: 'block', mt: 1 }}>
          Chi altro conosci di questo giro? Collegare le persone ti fa ritrovare il
          gruppo anche fra un anno, quando i nomi da soli non basteranno.
        </Typography>
      )}
    </Paper>
  );
};
